import { useState, type JSX } from 'react';
import { cn } from '@/lib/utils';
import { Pill, type PillTone } from '@/components/ui/pill';
import {
  DataTable,
  DataTableCell,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableRow,
} from '@/components/ui/data-table';
import { Segmented } from '../workspace/ui';
import {
  DIMENSION_LABELS,
  STATUS_LABELS,
  funnelSteps,
  rate,
  segmentLabel,
  sequenceStages,
  sortBreakdown,
} from './model';
import {
  BREAKDOWN_DIMENSIONS,
  type BreakdownDimension,
  type Breakdowns,
  type EmailProvider,
  type EmailSendStatus,
  type Funnel,
  type SequenceProgress,
} from './types';

/**
 * Where everyone is in the email sequence: not sent yet, on email 1, 2…,
 * finished. Each tile filters the recipients table.
 */
export function SequenceStrip({
  progress,
  stepCount,
  onPick,
  active,
}: {
  progress: SequenceProgress;
  stepCount?: number | null;
  onPick: (stage: string) => void;
  active?: string;
}): JSX.Element {
  const stages = sequenceStages(progress, stepCount);
  const total = stages.reduce((n, s) => n + s.count, 0);
  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">Sequence</h2>
        <p className="text-sm text-muted-foreground">
          Where each creator is{stepCount ? ` in the ${stepCount}-email sequence` : ''}. Replies, bounces and unsubscribes end it early.
        </p>
      </div>
      <SequenceBar progress={progress} stepCount={stepCount} className="h-2" />
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {stages.map((s) => {
          const isActive = active === s.key;
          return (
            <button
              key={s.key}
              type="button"
              aria-pressed={isActive}
              onClick={() => onPick(isActive ? '' : s.key)}
              className={cn('panel rounded-xl px-3 py-2.5 text-left transition-colors', isActive ? 'border-brand/50 bg-brand/10' : 'hover:bg-accent/40')}
            >
              <div className="flex items-baseline gap-2">
                <span className={cn('font-mono text-xl font-semibold tabular-nums', isActive && 'text-brand')}>{s.count.toLocaleString()}</span>
                <span className="text-sm text-muted-foreground tabular-nums">{rate(s.count, total)}</span>
              </div>
              <div className="text-sm font-medium">{s.label}</div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

/** The sequence as one stacked bar, darker the further along. Title lists every stage. */
export function SequenceBar({
  progress,
  stepCount,
  className,
}: {
  progress: SequenceProgress;
  stepCount?: number | null;
  className?: string;
}): JSX.Element {
  const stages = sequenceStages(progress, stepCount);
  const total = stages.reduce((n, s) => n + s.count, 0);
  const last = stages.length - 1;
  return (
    <div
      className={cn('flex w-full overflow-hidden rounded-full bg-muted', className ?? 'h-1.5')}
      role="img"
      aria-label={stages.map((s) => `${s.label}: ${s.count}`).join(', ')}
      title={stages.map((s) => `${s.label}: ${s.count.toLocaleString()}`).join('\n')}
    >
      {total > 0 &&
        stages.map((s, i) =>
          s.count > 0 ? (
            <div
              key={s.key}
              className="h-full bg-[var(--brand)]"
              style={{ width: `${(s.count / total) * 100}%`, opacity: i === 0 ? 0.15 : i === last ? 1 : 0.3 + (0.55 * i) / Math.max(1, last) }}
            />
          ) : null,
        )}
    </div>
  );
}

/** Save a blob the server sent as a file. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * The funnel as a row of counts. Every rate is a share of emails SENT, so the
 * steps can be compared with each other and across campaigns.
 */
export function FunnelStrip({
  funnel,
  provider,
  onPick,
  active,
}: {
  funnel: Funnel;
  provider: EmailProvider;
  /** Makes each step a filter for the recipients table. */
  onPick?: (stage: string) => void;
  active?: string;
}): JSX.Element {
  const steps = funnelSteps(funnel, provider);
  return (
    <div className={cn('grid gap-2', provider === 'csv' ? 'grid-cols-3 sm:max-w-xl' : 'grid-cols-2 sm:grid-cols-4 lg:grid-cols-7')}>
      {steps.map((s) => {
        const isActive = active === s.key;
        const body = (
          <>
            <div className="flex items-baseline gap-2">
              <span className={cn('font-mono text-xl font-semibold tabular-nums', isActive && 'text-brand')}>
                {s.count.toLocaleString()}
              </span>
              {s.ofSent && <span className="text-sm text-muted-foreground tabular-nums">{s.ofSent}</span>}
            </div>
            <div className="text-sm font-medium">{s.label}</div>
            <div className="truncate text-sm text-muted-foreground">{s.hint}</div>
          </>
        );
        return onPick ? (
          <button
            key={s.key}
            type="button"
            aria-pressed={isActive}
            onClick={() => onPick(isActive ? '' : s.key)}
            className={cn('panel rounded-xl px-3 py-2.5 text-left transition-colors', isActive ? 'border-brand/50 bg-brand/10' : 'hover:bg-accent/40')}
          >
            {body}
          </button>
        ) : (
          <div key={s.key} className="panel rounded-xl px-3 py-2.5">
            {body}
          </div>
        );
      })}
    </div>
  );
}

/**
 * The funnel split by one segment. This is the table that answers "which kind
 * of creator is worth emailing": same columns per row, rates over sent.
 */
export function BreakdownTable({ breakdowns, provider }: { breakdowns: Breakdowns; provider: EmailProvider }): JSX.Element {
  const [dimension, setDimension] = useState<BreakdownDimension>('kind');
  const rows = sortBreakdown(dimension, breakdowns[dimension] ?? []);
  const csv = provider === 'csv';
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-semibold">Results by segment</h2>
        <Segmented
          label="Split results by"
          value={dimension}
          onChange={setDimension}
          options={BREAKDOWN_DIMENSIONS.map((d) => ({ value: d, label: DIMENSION_LABELS[d] }))}
        />
      </div>
      <DataTable>
        <DataTableHeader>
          <tr>
            <DataTableHeaderCell>{DIMENSION_LABELS[dimension]}</DataTableHeaderCell>
            <DataTableHeaderCell numeric>{csv ? 'Exported' : 'In campaign'}</DataTableHeaderCell>
            {!csv && (
              <>
                <DataTableHeaderCell numeric>Sent</DataTableHeaderCell>
                <DataTableHeaderCell numeric>Opened</DataTableHeaderCell>
                <DataTableHeaderCell numeric>Clicked</DataTableHeaderCell>
                <DataTableHeaderCell numeric>Replied</DataTableHeaderCell>
                <DataTableHeaderCell numeric>Bounced</DataTableHeaderCell>
              </>
            )}
            <DataTableHeaderCell numeric>Joined</DataTableHeaderCell>
            <DataTableHeaderCell numeric>Posted</DataTableHeaderCell>
          </tr>
        </DataTableHeader>
        <tbody>
          {rows.length === 0 && (
            <DataTableRow>
              <DataTableCell colSpan={csv ? 4 : 9} muted>
                Nothing here yet.
              </DataTableCell>
            </DataTableRow>
          )}
          {rows.map((r) => {
            const base = csv ? r.added : r.sent;
            return (
              <DataTableRow key={r.key}>
                <DataTableCell anchor data-label={DIMENSION_LABELS[dimension]}>
                  {segmentLabel(dimension, r.key)}
                </DataTableCell>
                <DataTableCell numeric data-label={csv ? 'Exported' : 'In campaign'}>{r.added.toLocaleString()}</DataTableCell>
                {!csv && (
                  <>
                    <DataTableCell numeric data-label="Sent">{r.sent.toLocaleString()}</DataTableCell>
                    <CountRate label="Opened" count={r.opened} of={r.sent} />
                    <CountRate label="Clicked" count={r.clicked} of={r.sent} />
                    <CountRate label="Replied" count={r.replied} of={r.sent} />
                    <CountRate label="Bounced" count={r.bounced} of={r.sent} />
                  </>
                )}
                <CountRate label="Joined" count={r.joined} of={base} strong />
                <CountRate label="Posted" count={r.posted ?? 0} of={base} strong />
              </DataTableRow>
            );
          })}
        </tbody>
      </DataTable>
      {!csv && (
        <p className="text-sm text-muted-foreground">
          Percentages are shares of emails sent. Segments are as each creator looked when they were added.
        </p>
      )}
    </section>
  );
}

function CountRate({ label, count, of, strong }: { label: string; count: number; of: number; strong?: boolean }): JSX.Element {
  return (
    <DataTableCell numeric data-label={label}>
      <span className={cn('tabular-nums', strong && count > 0 && 'font-semibold')}>{count.toLocaleString()}</span>
      <span className="ml-1.5 inline-block w-10 text-left text-sm text-muted-foreground tabular-nums">{rate(count, of)}</span>
    </DataTableCell>
  );
}

const STATUS_TONES: Record<EmailSendStatus, PillTone> = {
  queued: 'pending',
  pushed: 'muted',
  exported: 'muted',
  sent: 'submitted',
  opened: 'info',
  clicked: 'info',
  replied: 'success',
  bounced: 'failed',
  unsubscribed: 'rejected',
  skipped: 'muted',
  failed: 'destructive',
};

export function SendStatusPill({ status, joined, posted }: { status: EmailSendStatus; joined?: boolean; posted?: boolean }): JSX.Element {
  if (posted) return <Pill tone="success">Posted</Pill>;
  if (joined) return <Pill tone="verified">Joined</Pill>;
  return <Pill tone={STATUS_TONES[status]}>{STATUS_LABELS[status]}</Pill>;
}

/** "3 days ago" for anything recent, the date otherwise. */
export function when(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)} hr ago`;
  if (mins < 60 * 24 * 14) {
    const days = Math.round(mins / (60 * 24));
    return days === 1 ? 'yesterday' : `${days} days ago`;
  }
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}
